import { Component } from "@theme/component";
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  CartUpdateEvent as _CartUpdateEvent,
  VariantUpdateEvent as _VariantUpdateEvent,
} from "@theme/events";
import { morph } from "@theme/morph";
import { cartPerformance } from "@theme/performance";
import {
  fetchConfig,
  formatCountTranslation,
  preloadImage,
  onAnimationEnd,
  prefersReducedMotion,
  yieldToMainThread,
  readDurationToken,
} from "@theme/utilities";

const ADDED_DWELL_FALLBACK_MS = 800;
// Longest checkmark exit is 210ms (70ms delay + 140ms fade); the small buffer
// keeps the control locked through the final painted frame.
const ADDED_EXIT_GUARD_MS = 240;
const PRESS_FEEDBACK_HOLD_FALLBACK_MS = 110;
const PRESS_FEEDBACK_RELEASE_MS = 240;

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10_000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

const HIDDEN_CLASS = "hidden";
const FORM_FIELD_QUANTITY = "quantity";
const STRING_TRUE = "true";
const ADD_TO_CART_COMPONENT = "add-to-cart-component";
const ADD_TO_CART_BUTTON_SELECTOR = '[ref="addToCartButton"]';
const PRODUCT_FORM_COMPONENT = "product-form-component";
const STICKY_ADD_TO_CART_COMPONENT = "sticky-add-to-cart";
const ADD_TO_CART_STATE_ATTRIBUTE = "addToCartState";
const TRANSACTION_STATE_BUSY = "busy";
const TRANSACTION_STATE_SUCCESS = "success";
const TRANSACTION_STATE_ERROR = "error";
const ADD_TO_CART_ERROR_FALLBACK = "Error adding to cart";

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {HTMLElement & {
 *   setAddToCartTransactionState: (state: "busy" | "success" | "error") => void,
 *   animateAddToCartSuccess: (options?: {flyToCart?: boolean}) => Promise<void>,
 *   settleAddToCartTransaction: () => void
 * }} StickyAddToCartElement
 */

/**
 * @typedef {object} AddToCartControlSnapshot
 * @property {HTMLButtonElement} control
 * @property {boolean} disabled
 * @property {string | null} ariaBusy
 * @property {string | undefined} state
 */

/**
 * @typedef {object} AddToCartTransaction
 * @property {string} variantId
 * @property {string | undefined} productId
 * @property {number} quantity
 * @property {number | null} previousCartQuantity
 * @property {HTMLButtonElement} initiator
 * @property {Event | undefined} event
 * @property {HTMLFormElement} form
 * @property {AddToCartControlSnapshot[]} controls
 * @property {StickyAddToCartElement[]} stickyComponents
 * @property {string | null} formAriaBusy
 * @property {Promise<void>} tapFeedback
 * @property {Promise<void> | null} operation
 * @property {"busy" | "success" | "error"} state
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"];

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) {
      return;
    }

    preloadImage(image);
  };

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async () => {
    const { addToCartButton } = this.refs;

    if (addToCartButton.dataset.added !== STRING_TRUE) {
      addToCartButton.dataset.added = STRING_TRUE;
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Keep the transaction locked for the complete confirmation: entrance,
    // dwell, and exit. The PDP scopes a shorter dwell through its CSS token.
    await new Promise(resolve =>
      setTimeout(resolve, readDurationToken("--add-to-cart-dwell-duration", ADDED_DWELL_FALLBACK_MS, this)),
    );

    addToCartButton.dataset.addedExiting = STRING_TRUE;
    delete addToCartButton.dataset.added;
    await yieldToMainThread();
    await Promise.all([
      onAnimationEnd(addToCartButton),
      new Promise(resolve => setTimeout(resolve, ADDED_EXIT_GUARD_MS)),
    ]);
    delete addToCartButton.dataset.addedExiting;
  };

  /**
   * Starts the success motion after the form-owned transaction has received a
   * successful response. Only the initiating control gets a fly-to-cart capsule;
   * all synchronized controls still expose the same Added state.
   * @param {{flyToCart?: boolean}} [options]
   */
  animateAddToCartSuccess = async ({ flyToCart = false } = {}) => {
    const animationEnabled = this.dataset.addToCartAnimation === STRING_TRUE;
    if (flyToCart && animationEnabled) {
      this.#animateFlyToCart();
    }

    await this.animateAddToCart();
  };

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("pointerenter", this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener("pointerenter", this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent} event - The click event.
   */
  handleClick(event) {
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest(PRODUCT_FORM_COMPONENT));
    const initiator = event.target instanceof Element ? event.target.closest("button") : null;

    if (!productForm || !(initiator instanceof HTMLButtonElement)) {
      return;
    }

    // Own pointer/keyboard button activation here so the lock is acquired before
    // native submit dispatch can create a second path into the same mutation.
    event.preventDefault();
    productForm.requestAddToCart(initiator, event);
  }

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector(".header-actions__cart-icon");

    const image = this.dataset.productVariantMedia;

    // fly-to-cart.js is only loaded on product templates (snippets/scripts.liquid). Appending an
    // UNDEFINED <fly-to-cart> elsewhere strands a never-upgraded element: its self-removal never
    // runs, base.css keeps the capsule visible, and cart-icon.js stalls every bubble pop waiting
    // for a fly-to-cart:landed event that can never fire.
    if (!cartIcon || !addToCartButton || !image || !customElements.get("fly-to-cart")) {
      return;
    }

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement("fly-to-cart"));

    flyToCartElement.classList.add("fly-to-cart--main");
    flyToCartElement.style.setProperty("background-image", `url(${image})`);
    flyToCartElement.style.setProperty("--start-opacity", "0");
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.append(flyToCartElement);
  }
}

if (!customElements.get(ADD_TO_CART_COMPONENT)) {
  customElements.define(ADD_TO_CART_COMPONENT, AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"];
  #abortController = new AbortController();

  /**
   * The one form-owned add transaction. It is assigned synchronously before
   * animation or fetch, so primary and sticky activations share one lock.
   * @type {AddToCartTransaction | null}
   */
  #activeAddToCartTransaction = null;

  /** @type {number | undefined} */
  #timeout;

  /** @type {string | null} */
  #latestSelectedOptionId = null;

  /** @type {_VariantUpdateEvent | null} */
  #pendingVariantUpdate = null;

  // The visible picker changes before its authoritative variant response arrives.
  // Keep purchase controls locked during that interval so an add cannot capture
  // the previous hidden variant ID.
  #variantChangeInProgress = false;

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {_CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async event => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === PRODUCT_FORM_COMPONENT) {
      return;
    }

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * @param {_VariantUpdateEvent} event
   */
  #onVariantUpdate = async event => {
    if (this.#latestSelectedOptionId && event.detail.sourceId !== this.#latestSelectedOptionId) {
      return;
    }

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId === this.dataset.productId) {
      // productId matches, continue processing
    } else {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? "";

    // The selected value is ready for the NEXT transaction immediately, but
    // purchase controls retain the captured transaction's label/state until it
    // settles. This prevents an in-flight add from being visually relabelled.
    if (this.#activeAddToCartTransaction) {
      this.#pendingVariantUpdate = event;
      return;
    }

    await this.#applyVariantUpdateAndSettle(event);
  };

  /** @param {_VariantUpdateEvent} event */
  #applyVariantUpdateAndSettle = async event => {
    await this.#applyVariantUpdate(event);
    this.#settleVariantChange();
  };

  /** @param {_VariantUpdateEvent} event */
  #applyVariantUpdate = async event => {
    this.#updateAddToCartButton(event);

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector(".quantity-rules");
    const isQuantityRulesChanging = Boolean(quantityRules) !== Boolean(newQuantityRules);

    const newPricePerItem = event.detail.data.html.querySelector("price-per-item");
    const isPricePerItemChanging = Boolean(pricePerItem) !== Boolean(newPricePerItem);

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector(".product-form-buttons");

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step,
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        [".quantity-label", quantityLabel, quantitySelector],
        [".quantity-rules", quantityRules, this.refs.productFormButtons],
        ["price-per-item", pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector("volume-pricing");
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) {
      return;
    }

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} event */
  #onVariantSelected = event => {
    const eventTargetProductId = event.target instanceof HTMLElement ? event.target.dataset.productId : undefined;
    if (eventTargetProductId && eventTargetProductId !== this.dataset.productId) {
      return;
    }

    this.#latestSelectedOptionId = event.detail?.resource?.id ?? null;
    if (this.#pendingVariantUpdate?.detail.sourceId !== this.#latestSelectedOptionId) {
      this.#pendingVariantUpdate = null;
    }

    this.#startVariantChange();
  };

  /** @param {_VariantUpdateEvent} event */
  #updateAddToCartButton(event) {
    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) {
      return;
    }

    // Update the button state
    const variantAvailable = event.detail.resource !== null && event.detail.resource.available !== false;

    if (variantAvailable) {
      currentAddToCartButtonContainer.enable();
    } else {
      currentAddToCartButtonContainer.disable();
    }

    this.refs.quantitySelector?.setAvailability?.(variantAvailable);

    const newProductForm = [...event.detail.data.html.querySelectorAll(PRODUCT_FORM_COMPONENT)].find(
      productForm =>
        productForm instanceof HTMLElement &&
        productForm.dataset.sectionId === this.dataset.sectionId &&
        productForm.dataset.productId === this.dataset.productId,
    );
    const newAddToCartButton = newProductForm?.querySelector(ADD_TO_CART_BUTTON_SELECTOR);
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource === null || event.detail.resource.available === false) {
        acceleratedCheckoutButtonContainer?.setAttribute("hidden", STRING_TRUE);
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          "data-product-variant-media",
          `${productVariantMedia}&width=100`,
        );
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();

    // Recreate per connect: disconnectedCallback aborts the controller, and addEventListener
    // with an already-aborted signal is a silent no-op after a morph reconnect.
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog");
    target?.addEventListener(
      ThemeEvents.variantUpdate,
      /** @type {EventListener} */ (/** @type {any} */ (this.#onVariantUpdate)),
      { signal },
    );
    target?.addEventListener(
      ThemeEvents.variantSelected,
      /** @type {EventListener} */ (/** @type {any} */ (this.#onVariantSelected)),
      { signal },
    );

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(
      ThemeEvents.cartUpdate,
      /** @type {EventListener} */ (/** @type {any} */ (this.#onCartUpdate)),
      { signal },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }
  }

  /**
   * Updates quantity selector with cart data for a specific variant. The UI is
   * changed only when that variant is still selected; an in-flight transaction
   * for an older variant must not overwrite the newly selected variant's count.
   * @param {Cart} cart - The cart object with items array
   * @param {string} [variantId]
   * @returns {number} The cart quantity for the requested variant
   */
  #updateCartQuantityFromData(cart, variantId) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    const requestedVariantId = variantId || variantIdInput?.value;
    if (!requestedVariantId || !cart?.items) {
      return 0;
    }

    const cartItem = cart.items.find(item => item.variant_id.toString() === requestedVariantId.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    if (variantIdInput?.value.toString() !== requestedVariantId.toString()) {
      return cartQty;
    }

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector("quantity-selector-component"));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @param {string} [variantId]
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity(variantId) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantId && !variantIdInput?.value) {
      return null;
    }

    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart, variantId);
      return cart;
    } catch (error) {
      // Log the error
      console.error("Failed to fetch cart quantity:", error);
      return null;
    }
  }

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();
    const submitter = event instanceof SubmitEvent ? event.submitter : null;
    const initiator =
      submitter instanceof HTMLButtonElement ? submitter : this.refs.addToCartButtonContainer?.refs.addToCartButton;

    if (initiator) {
      this.requestAddToCart(initiator, event);
    }
  }

  /**
   * Requests the one shared add-to-cart transaction. The lock and immutable
   * variant/quantity snapshot are created synchronously; any concurrent primary
   * or sticky activation receives the existing operation and performs no work.
   * @param {HTMLButtonElement} initiator
   * @param {Event} [event]
   * @returns {Promise<void>}
   */
  requestAddToCart(initiator, event) {
    if (this.#variantChangeInProgress) {
      return Promise.resolve();
    }

    // The native disabled attribute is the primary interaction lock. This
    // state guard also prevents a second mutation if another component replaces
    // or re-enables the control before the visible confirmation has fully exited.
    if (initiator.dataset.added === STRING_TRUE || initiator.dataset.addedExiting === STRING_TRUE) {
      return Promise.resolve();
    }

    if (this.#activeAddToCartTransaction) {
      return this.#activeAddToCartTransaction.operation ?? Promise.resolve();
    }

    const form = this.querySelector("form");
    const variantId = this.#getIntendedVariantId();
    if (!(form instanceof HTMLFormElement) || !variantId) {
      return Promise.resolve();
    }

    const stickyComponents = this.#getStickyAddToCartComponents();
    const controlElements = new Set([
      ...this.querySelectorAll(`${ADD_TO_CART_COMPONENT} ${ADD_TO_CART_BUTTON_SELECTOR}`),
      ...stickyComponents.flatMap(component => [...component.querySelectorAll(ADD_TO_CART_BUTTON_SELECTOR)]),
    ]);
    const controls = [...controlElements]
      .filter(control => control instanceof HTMLButtonElement)
      .map(control => ({
        control,
        disabled: control.disabled,
        ariaBusy: control.getAttribute("aria-busy"),
        state: control.dataset[ADD_TO_CART_STATE_ATTRIBUTE],
      }));

    /** @type {AddToCartTransaction} */
    const transaction = {
      variantId,
      initiator,
      event,
      form,
      controls,
      stickyComponents,
      productId: this.dataset.productId,
      quantity: this.#getQuantity(),
      previousCartQuantity: this.refs.quantitySelector?.getCurrentValues?.()?.cartQuantity ?? null,
      formAriaBusy: this.getAttribute("aria-busy"),
      tapFeedback: this.#playPressFeedback(initiator, event),
      operation: null,
      state: TRANSACTION_STATE_BUSY,
    };

    this.#activeAddToCartTransaction = transaction;
    transaction.operation = this.#processAddToCart(transaction);
    return transaction.operation;
  }

  /**
   * Guarantees one painted press pulse for pointer-activated PDP and tea-card
   * purchases. The cart request runs concurrently; only an unusually fast
   * success waits for it.
   * @param {HTMLButtonElement} initiator
   * @param {Event | undefined} event
   * @returns {Promise<void>}
   */
  async #playPressFeedback(initiator, event) {
    if (
      !this.closest(".product-information, tea-card-component") ||
      prefersReducedMotion() ||
      !(event instanceof MouseEvent) ||
      event.detail === 0
    ) {
      return;
    }

    initiator.dataset.tapFeedback = STRING_TRUE;
    await new Promise(resolve => requestAnimationFrame(resolve));
    await new Promise(resolve =>
      setTimeout(
        resolve,
        readDurationToken("--button-press-feedback-hold-duration", PRESS_FEEDBACK_HOLD_FALLBACK_MS, initiator),
      ),
    );
    delete initiator.dataset.tapFeedback;
    await new Promise(resolve => setTimeout(resolve, PRESS_FEEDBACK_RELEASE_MS));
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    // Each product form owns its variant input. A product page URL can also carry
    // `?variant=`, but that describes only the main PDP product; using it for a
    // nested product form would submit the PDP variant instead.
    return this.refs.variantId?.value || new URL(globalThis.location.href).searchParams.get("variant") || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * Validates the captured transaction without mutating its variant or quantity.
   * @param {AddToCartTransaction} transaction
   * @returns {{valid: boolean, restoreFocus: boolean}}
   */
  #validateAddToCart(transaction) {
    if (transaction.controls.some(({ disabled }) => disabled)) {
      return { valid: false, restoreFocus: false };
    }

    if (!transaction.form.checkValidity()) {
      transaction.form.reportValidity();
      return { valid: false, restoreFocus: false };
    }

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        const errorTemplate = this.dataset.quantityErrorMax || "";
        const errorMessage = errorTemplate.replace("{{ maximum }}", validation.maxQuantity?.toString() || "");
        this.#showAddToCartError(errorMessage || this.#getAddToCartErrorMessage(transaction));
        return { valid: false, restoreFocus: true };
      }
    }

    return { valid: true, restoreFocus: false };
  }

  /**
   * @param {AddToCartTransaction} transaction
   */
  #prepareAddToCartForm(transaction) {
    const formData = new FormData(transaction.form);
    formData.set("id", transaction.variantId);
    formData.set(FORM_FIELD_QUANTITY, transaction.quantity.toString());

    const cartItemsComponents = document.querySelectorAll("cart-items-component");
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }
    formData.append("sections", cartItemComponentsSectionIds.join(","));

    return formData;
  }

  /**
   * @param {AddToCartTransaction} transaction
   */
  async #processAddToCart(transaction) {
    const { addToCartTextError } = this.refs;
    let shouldRestoreFocus = false;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }

    try {
      const validation = this.#validateAddToCart(transaction);
      shouldRestoreFocus = validation.restoreFocus;
      if (!validation.valid) {
        this.#setAddToCartTransactionState(transaction, TRANSACTION_STATE_ERROR);
        return;
      }

      this.#setAddToCartTransactionState(transaction, TRANSACTION_STATE_BUSY);
      const formData = this.#prepareAddToCartForm(transaction);
      const fetchCfg = fetchConfig("javascript", { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: "application/json",
        },
        signal: this.#abortController.signal,
      });
      const responseJson = await response.json();

      if (responseJson.status) {
        shouldRestoreFocus = true;
        await this.#handleCartError(transaction, responseJson);
        this.#setAddToCartTransactionState(transaction, TRANSACTION_STATE_ERROR);
      } else {
        await transaction.tapFeedback;
        this.#setAddToCartTransactionState(transaction, TRANSACTION_STATE_SUCCESS);
        await Promise.all([
          this.#handleCartSuccess(transaction, addToCartTextError ?? null, formData, responseJson),
          this.#animateAddToCartSuccess(transaction),
        ]);
      }
    } catch (error) {
      if (!(error instanceof Error && error.name === "AbortError")) {
        console.error(error);
        const message = this.#getAddToCartErrorMessage(transaction);
        this.dispatchEvent(new CartErrorEvent(transaction.form.id, message, message, {}));
        this.#showAddToCartError(message);
        shouldRestoreFocus = true;
        this.#setAddToCartTransactionState(transaction, TRANSACTION_STATE_ERROR);
      }
    } finally {
      await transaction.tapFeedback;

      if (transaction.event) {
        cartPerformance.measureFromEvent("add:user-action", transaction.event);
      }

      this.#settleAddToCartTransaction(transaction, shouldRestoreFocus);
    }
  }

  /** @returns {StickyAddToCartElement[]} */
  #getStickyAddToCartComponents() {
    const scope = this.closest(".shopify-section");
    if (!scope || !this.dataset.productId) {
      return [];
    }

    return /** @type {StickyAddToCartElement[]} */ (
      [...scope.querySelectorAll(STICKY_ADD_TO_CART_COMPONENT)].filter(
        component => component instanceof HTMLElement && component.dataset.productId === this.dataset.productId,
      )
    );
  }

  /**
   * Keeps every purchase control on the same transaction state.
   * @param {AddToCartTransaction} transaction
   * @param {"busy" | "success" | "error"} state
   */
  #setAddToCartTransactionState(transaction, state) {
    transaction.state = state;
    this.dataset[ADD_TO_CART_STATE_ATTRIBUTE] = state;
    this.setAttribute("aria-busy", STRING_TRUE);

    for (const { control } of transaction.controls) {
      control.dataset[ADD_TO_CART_STATE_ATTRIBUTE] = state;
      control.disabled = true;
      control.setAttribute("aria-busy", STRING_TRUE);
    }

    for (const component of transaction.stickyComponents) {
      component.dataset[ADD_TO_CART_STATE_ATTRIBUTE] = state;
      component.setAddToCartTransactionState(state);
    }
  }

  /**
   * Runs synchronized success badges while limiting fly-to-cart motion to the
   * control that initiated the captured transaction.
   * @param {AddToCartTransaction} transaction
   */
  async #animateAddToCartSuccess(transaction) {
    const animations = [];
    const addToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll(ADD_TO_CART_COMPONENT)
    );

    for (const container of addToCartContainers) {
      animations.push(
        container.animateAddToCartSuccess({
          flyToCart: container.contains(transaction.initiator),
        }),
      );
    }

    for (const component of transaction.stickyComponents) {
      animations.push(
        component.animateAddToCartSuccess({
          flyToCart: component.contains(transaction.initiator),
        }),
      );
    }

    await Promise.allSettled(animations);
  }

  /**
   * Restores exact pre-transaction control state, recovers focus on failure,
   * releases the lock, then applies the latest deferred variant relabel.
   * @param {AddToCartTransaction} transaction
   * @param {boolean} shouldRestoreFocus
   */
  #settleAddToCartTransaction(transaction, shouldRestoreFocus) {
    for (const { control, disabled, ariaBusy, state } of transaction.controls) {
      control.disabled = disabled;
      if (ariaBusy === null) {
        control.removeAttribute("aria-busy");
      } else {
        control.setAttribute("aria-busy", ariaBusy);
      }

      if (state === undefined) {
        delete control.dataset[ADD_TO_CART_STATE_ATTRIBUTE];
      } else {
        control.dataset[ADD_TO_CART_STATE_ATTRIBUTE] = state;
      }
    }

    if (transaction.formAriaBusy === null) {
      this.removeAttribute("aria-busy");
    } else {
      this.setAttribute("aria-busy", transaction.formAriaBusy);
    }
    delete this.dataset[ADD_TO_CART_STATE_ATTRIBUTE];

    if (shouldRestoreFocus && transaction.initiator.isConnected) {
      transaction.initiator.focus({ preventScroll: true });
    }

    if (this.#activeAddToCartTransaction === transaction) {
      this.#activeAddToCartTransaction = null;
    }

    for (const component of transaction.stickyComponents) {
      delete component.dataset[ADD_TO_CART_STATE_ATTRIBUTE];
      component.settleAddToCartTransaction();
    }

    const pendingVariantUpdate = this.#pendingVariantUpdate;
    this.#pendingVariantUpdate = null;
    if (
      pendingVariantUpdate &&
      (!this.#latestSelectedOptionId || pendingVariantUpdate.detail.sourceId === this.#latestSelectedOptionId)
    ) {
      this.#applyVariantUpdateAndSettle(pendingVariantUpdate);
    }
  }

  #startVariantChange() {
    this.#variantChangeInProgress = true;
    this.setAttribute("aria-busy", STRING_TRUE);

    const controls = [
      ...this.querySelectorAll(`${ADD_TO_CART_COMPONENT} ${ADD_TO_CART_BUTTON_SELECTOR}`),
      ...this.#getStickyAddToCartComponents().flatMap(component => [
        ...component.querySelectorAll(ADD_TO_CART_BUTTON_SELECTOR),
      ]),
    ];

    for (const control of controls) {
      if (control instanceof HTMLButtonElement) {
        control.disabled = true;
        control.setAttribute("aria-busy", STRING_TRUE);
        control.dataset.variantPending = STRING_TRUE;
      }
    }
  }

  #settleVariantChange() {
    if (!this.#variantChangeInProgress || this.#activeAddToCartTransaction) {
      return;
    }

    this.#variantChangeInProgress = false;
    this.removeAttribute("aria-busy");

    const controls = [
      ...this.querySelectorAll(`${ADD_TO_CART_COMPONENT} ${ADD_TO_CART_BUTTON_SELECTOR}`),
      ...this.#getStickyAddToCartComponents().flatMap(component => [
        ...component.querySelectorAll(ADD_TO_CART_BUTTON_SELECTOR),
      ]),
    ];

    for (const control of controls) {
      if (control instanceof HTMLButtonElement) {
        control.removeAttribute("aria-busy");
        delete control.dataset.variantPending;
      }
    }
  }

  /** @param {AddToCartTransaction} transaction */
  #getAddToCartErrorMessage(transaction) {
    const localizedMessage = transaction.stickyComponents.find(component => component.dataset.addToCartError)?.dataset
      .addToCartError;
    return localizedMessage || ADD_TO_CART_ERROR_FALLBACK;
  }

  /** @param {string} message */
  #showAddToCartError(message) {
    const { addToCartTextError } = this.refs;
    if (addToCartTextError) {
      addToCartTextError.classList.remove(HIDDEN_CLASS);
      const textNode = addToCartTextError.childNodes[2];
      if (textNode) {
        textNode.textContent = message;
      } else {
        addToCartTextError.append(document.createTextNode(message));
      }
    }

    this.#setLiveRegionText(message);
    clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      addToCartTextError?.classList.add(HIDDEN_CLASS);
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * Handles a cart error response: dispatches a CartErrorEvent, updates the
   * error UI element, sets the live region text, and dispatches a CartAddEvent
   * with didError=true so other components can update their state.
   * @param {AddToCartTransaction} transaction
   * @param {any} responseJson
   */
  async #handleCartError(transaction, responseJson) {
    this.dispatchEvent(
      new CartErrorEvent(
        transaction.form.getAttribute("id") || "",
        responseJson.message,
        responseJson.description,
        responseJson.errors,
      ),
    );

    // Determine the accurate error message BEFORE displaying anything.
    // Shopify reports the total inventory cap (e.g. "Only 17 items were added")
    // but the user expects a delta-based message (e.g. "Only 1 item was added")
    // when items are partially added. Fetching the cart first avoids a flash
    // and keeps data-cart-quantity in sync for subsequent add attempts.
    let displayMessage = responseJson.message || this.#getAddToCartErrorMessage(transaction);
    let actuallyAdded = 0;
    const previousCartQty = transaction.previousCartQuantity;

    if (previousCartQty !== null) {
      try {
        const cartResponse = await fetch("/cart.js");
        const cart = await cartResponse.json();
        const variantIdValue = transaction.variantId;

        if (variantIdValue) {
          const cartItem = cart.items.find(
            /** @param {any} item */ item => item.variant_id.toString() === variantIdValue,
          );
          const newCartQty = cartItem ? cartItem.quantity : 0;
          actuallyAdded = newCartQty - previousCartQty;

          if (actuallyAdded > 0) {
            displayMessage = formatCountTranslation("partial_add_to_cart", actuallyAdded, displayMessage);
          }

          // Update cart quantity so subsequent adds use the correct baseline
          this.refs.quantitySelector?.setCartQuantity?.(newCartQty);
        }
      } catch {
        // Use Shopify's default message on fetch failure
      }
    }

    this.#showAddToCartError(displayMessage);

    // Only dispatch a cart update event if items were actually added — dispatching
    // with itemCount 0 causes the cart icon bubble to briefly hide and reappear.
    if (actuallyAdded > 0) {
      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          didError: true,
          source: PRODUCT_FORM_COMPONENT,
          itemCount: actuallyAdded,
          productId: transaction.productId,
        }),
      );
    }
  }

  /**
   * Handles a successful cart add response: clears the error UI, announces
   * the addition to screen readers, fetches updated cart quantities, and
   * dispatches a CartAddEvent so other components can update their state.
   * @param {HTMLElement | null} addToCartTextError
   * @param {AddToCartTransaction} transaction
   * @param {FormData} formData
   * @param {any} responseJson
   */
  async #handleCartSuccess(transaction, addToCartTextError, formData, responseJson) {
    const variantId = formData.get("id");

    if (addToCartTextError) {
      addToCartTextError.classList.add(HIDDEN_CLASS);
      addToCartTextError.removeAttribute("aria-live");
    }

    if (typeof variantId !== "string" || variantId === "") {
      throw new TypeError("Form ID is required");
    }

    // Add aria-live region to inform screen readers that the item was added
    // Get the added text from any add-to-cart button
    const anyAddToCartButton = transaction.controls[0]?.control;

    if (anyAddToCartButton) {
      const addedTextElement = anyAddToCartButton.querySelector(".add-to-cart-text--added");
      const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

      this.#setLiveRegionText(addedText);

      setTimeout(() => {
        this.#clearLiveRegionText();
      }, SUCCESS_MESSAGE_DISPLAY_DURATION);
    }

    // Fetch the updated cart to get the actual total quantity for this variant
    const cart = await this.#fetchAndUpdateCartQuantity(transaction.variantId);

    this.dispatchEvent(
      new CartAddEvent(cart ?? undefined, variantId, {
        source: PRODUCT_FORM_COMPONENT,
        itemCount: Number(formData.get(FORM_FIELD_QUANTITY)) || Number(this.dataset.quantityDefault),
        productId: transaction.productId,
        sections: responseJson.sections,
      }),
    );
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+([^)\s][^)]*)\)/);
      if (inCartText?.[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle(HIDDEN_CLASS, cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement("beforebegin", /** @type {Element} */ (newElement.cloneNode(true)));
    } else {
      // no-op: element absent both before and after update
    }
  }
}

if (!customElements.get(PRODUCT_FORM_COMPONENT)) {
  customElements.define(PRODUCT_FORM_COMPONENT, ProductFormComponent);
}
